import { History } from "history";
import React, { useState } from "react";
import { Route } from "react-router";
import styled from "styled-components";
import uuid from "uuid";
import { apiClient } from "../../App";
import Button from "../../components/Button";
import { FlashControl } from "../../components/FlashMessage";
import { libraryStorage } from "../../storage/LibraryStorage";
import MessageSequence from "../Recorder/MessageSequence";
import { useEscape } from "../../hooks/useEscape";
import json5 from "json5";
interface Props {
  history: History;
}

const StyledLibrary = styled.div`
  display: flex;

  .col-1 {
    width: 40vw;
    padding: 1rem 0.6rem 4rem 0.6rem;
    overflow-y: auto;
    max-height: 100vh;
    border-right: 2px solid ${(props) => props.theme.colors.gray2};

    textarea {
      margin: 1rem 0;
      width: 100%;
      height: 25vh;
      min-height: 300px;
      font-size: 0.9rem;
      border: 1px solid ${(props) => props.theme.colors.gray1};
      padding: 0.5rem 0.25rem;
      font-family: ${(props) => props.theme.monospaceFont};
    }

    .advanced {
      margin: 0.5rem 0 1rem 0;
      display: flex;
      align-items: center;

      > * {
        margin-right: 0.5rem;
        flex: 1;
      }
    }

    .response {
      background: ${(props) => props.theme.colors.gray2};
      padding: 0.5rem;
      border-radius: 4px;
      overflow-x: scroll;
      cursor: pointer;
    }
  }

  .col-2 {
    width: 60vw;
    height: 100vh;
    max-width: 100%;
  }

  .placeholder {
    font-size: 0.9rem;
    color: ${(props) => props.theme.colors.mutedText};
  }
`;

const StyledLibItem = styled.div`
  padding: 0.4rem 0;
  border-bottom: 1px solid ${(props) => props.theme.colors.gray2};

  .header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
  }

  .name {
    font-size: 0.9rem;
    flex: 1;

    .subject {
      font-size: 0.8rem;
      color: ${(props) => props.theme.colors.mutedText};
      margin-top: 0.2rem;
    }
  }

  .actions {
    display: flex;

    button {
      margin-left: 0.5rem;
    }
  }

  .expanded-message {
    margin-top: 0.5rem;
    font-size: 12px;
  }

  .hint {
    margin-top: 0.5rem;
    font-size: 0.6rem;
    text-align: center;
    color: ${(props) => props.theme.colors.mutedText};
  }
`;

const Library: React.FC<Props> = ({ history }) => {
  const [reqId, setReqId] = useState<string | null>(null);

  const [lib, setLib] = useState(libraryStorage.getAll() || []);
  const [expandedMessage, setExpandedMessage] = useState("");
  const [editing, setEditing] = useState("");
  const [editedMessage, setEditedMessage] = useState("");

  useEscape(() => {
    if (editing) {
      setEditing("");

      if (editedMessage) {
        let parsedMessage = null;

        try {
          parsedMessage = json5.parse(editedMessage);
        } catch (err) {
          FlashControl.showWarning("Invalid JSON");
          console.error("invalid json:", editedMessage);
        }

        if (parsedMessage) {
          setLib(libraryStorage.updateMessage(editing, parsedMessage));
        }

        setEditedMessage("");
      }
    }
  });

  const sendMessage = async (id: string) => {
    const message = lib.find((item) => item.id === id);

    if (!message) {
      return;
    }

    try {
      const reqId = uuid.v4();
      // Start recording for this particular reqId
      const { data: recording } = await apiClient.post<any>("/recording", {
        reqId,
      });

      // Post message on bus
      await apiClient.post("/bus", {
        message: { ...message.message, reqId },
        subject: message.subject,
        timeout: 10 * 1000,
        maxResponses: 0,
      });

      const pushPath = `/library/recording/${recording.id}/${reqId}`;
      history.push(pushPath);

      setReqId(message.message.reqId);

      setTimeout(() => {
        // TODO: This should probablybe backends responsibility
        apiClient.post(`/recording/${recording?.id}/stop`, {});
      }, 1000);
    } catch (err) {
      console.log(err);
      FlashControl.showWarning("Failed to send message");
    }
  };

  const handleDelete = (id: string) => {
    setLib(libraryStorage.delete(id));
  };

  const handleExpand = (id: string) => {
    setExpandedMessage(expandedMessage === id ? "" : id);
  };

  return (
    <StyledLibrary>
      <div className="col-1">
        {lib.map((item) => (
          <StyledLibItem key={item.id}>
            <div className="header-row">
              <div className="name" onClick={() => handleExpand(item.id)}>
                <div>{item.name}</div>
                <div className="subject">{item.subject}</div>
              </div>
              <div className="actions">
                <Button title="Send" size="s" onClick={() => sendMessage(item.id)} />
                <Button title="Delete" size="s" onClick={() => handleDelete(item.id)} destructive />
              </div>
            </div>
            {expandedMessage === item.id && (
              <div className="expanded-message" onDoubleClick={() => setEditing(item.id)}>
                <div className="response">
                  <pre
                    contentEditable={editing === item.id}
                    onInput={(e) => setEditedMessage(e.currentTarget.innerText)}
                    suppressContentEditableWarning
                    // dangerouslySetInnerHTML={{ __html: JSON.stringify(item.message, null, 4) }}
                  >
                    {JSON.stringify(item.message, null, 4)}
                  </pre>
                  <div className="hint">
                    {editing === item.id ? "Press (esc) to save changes" : "Double click message to edit"}
                  </div>
                </div>
              </div>
            )}
          </StyledLibItem>
        ))}
        {lib.length === 0 && <div className="placeholder">No messages saved</div>}
      </div>
      <div className="col-2">
        <Route
          path={`/library/recording/:id/:reqId`}
          render={(props) => <MessageSequence {...props} disableKeybindings />}
          key={reqId || "n/a"}
        />
      </div>
    </StyledLibrary>
  );
};

export default Library;
